import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { DraggingDirections } from "@v1/utils/editor";
import type { Hotkey } from "@v1/utils/getHotKey";
import type { Position } from "replo-runtime/shared/types";
import type { LengthValueType } from "replo-runtime/shared/utils/units";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type {
  PreviewableProperty,
  PreviewableSubProperty,
} from "schemas/preview";
import type { RuntimeStyleAttribute } from "schemas/styleAttribute";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import ChevronMenuIndicator from "@v1/components/common/designSystem/ChevronMenuIndicator";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import {
  clampMinMax,
  getDisplayValueFromMenuOptions,
} from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import {
  selectDraftComponents,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { setDraggingCursor } from "@v1/reducers/drag-and-drop-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import checkForMixedValues from "@v1/utils/checkForMixedValues";
import { getEdgeAttribute } from "@v1/utils/component";
import { getCursor } from "@v1/utils/editor";
import { getHotKey } from "@v1/utils/getHotKey";
import { useInCanvasPreview } from "@v1/utils/preview";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  CSS_LENGTH_TYPES,
  formatCommaSeparatedValues,
  isCursorOnFirstValue,
  parseCommaSeparatedValues,
  parseUnit,
} from "replo-runtime/shared/utils/units";
import {
  isMixedStyleValue,
  REPLO_MIXED_STYLE_VALUE,
} from "replo-runtime/store/utils/mixed-values";
import { parseFloat, round } from "replo-utils/lib/math";
import { useRequiredContext } from "replo-utils/react/context";
import { useEffectEvent } from "replo-utils/react/use-effect-event";

export type InputOption = {
  label: string;
  value: string | null;
  isDisabled?: boolean;
  /** Optional icon to display at the start of the menu item */
  icon?: React.ReactNode;
};

export type LengthInputModifierHotkey = "shiftKey" | "altKey";

/**
 * NOTE (Andrew, 2026-01-09, REPL-20714): Normalize user input to include px units.
 * "24, 20" -> "24px, 20px", "30px, 15" -> "30px, 15px"
 * Returns null if not a comma-separated value pattern.
 */
function normalizeCommaSeparatedInput(value: string): string | null {
  const match = value
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*(?:px)?\s*,\s*(-?\d+(?:\.\d+)?)\s*(?:px)?$/i);
  if (!match) {
    return null;
  }
  return `${match[1]}px, ${match[2]}px`;
}

type KeyEvent = {
  altKey: boolean;
  shiftKey: boolean;
  key?: string | undefined;
};

type DragTrigger = "label" | "startEnhancer" | "none";

/**
 * TODO (Noah, 2022-03-01, REPL-1038): We should actually have a real mapping
 * of the actual unit types like "%": number, "px": number etc here,
 * but we don't have an actual union type for the units yet
 */
type LengthInputBounds = Record<string, number>;
type CommonProps = {
  /** Functions to do something when the input is dragging */
  onDragEnd?(): void;
  onDragStart?(): void;
  /** Function to do something when you need to preview the change */
  onPreviewChange?(value: any, hotkey: LengthInputModifierHotkey | null): void;
  /** Function to do something when the value of the input changes (Required) */
  onChange(value: any, hotkey: LengthInputModifierHotkey | null): void;
  /** Function to use to transform the value in a custom way */
  transformValue?(value: any): any;
  /** Strings or icons inside the input */
  startEnhancer?: React.ReactNode;
  endEnhancer?(): React.ReactNode;
  /** Label of the input that will show on the left side of the input */
  label?: React.ReactNode;
  /** Name of the field used, useful in the modifier version. */
  field: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
  inputClassName?: string;
  backgroundColor?: string;
  placeholder?: string;
  /** Allow the dragging vertical or horizontal. */
  draggingType?: "horizontal" | "vertical";
  /** Allow dragging positive or negative. */
  draggingDirection?: DraggingDirections;
  /**
   * Part of the input from the drag is allowed, we should use "label" on most
   * cases.
   */
  // TODO (Sebas, 2024-09-09): Once we migrated to use the label trigger we should
  // remove the startEnhancer option.
  dragTrigger?: DragTrigger;
  /** Name of property to preview in the canvas */
  previewProperty?: PreviewableProperty;
  /** Name of properties to preview in the canvas when pressing the shift key */
  previewablePropertiesForShift?: PreviewableProperty[];
  /** Name of properties to preview in the canvas when pressing the option key */
  previewablePropertiesForOption?: PreviewableProperty[];
  /**
   * Name of a sub-property that is a part of parent property. e.g: rotateZ is
   * a sub-property of the transform property.
   */
  previewSubProperty?: PreviewableSubProperty;
  /**
   * Index of the property we want to preview. This is useful for properties
   * that are composed with multiple values, like box-shadow.
   */
  previewPropertyIndex?: number;
  /** Props to allow metrics/units for the input value */
  metrics?: string[];
  unitDefaults?: LengthValueType;
  allowsNegativeValue?: boolean;
  /**
   * Value to reset the input. This value will be passed to onSubmit when the input
   * is option-clicked. If this is undefined (or not passed), nothing will happen on
   * option-click. If this is null, onSubmit will be called with null when option-clicked.
   */
  resetValue?: string | null;
  /** Value from the drag start if there is no value */
  anchorValue?: string;
  /** Options to use as preset values */
  menuOptions?: InputOption[];
  /** Values to use as min and max when you type a value. Uses clampMaxMin function */
  maxValues?: LengthInputBounds;
  minValues?: LengthInputBounds;
  /** Values to use as min and max when you drag the input. Uses clampMaxMin function */
  maxDragValues?: LengthInputBounds;
  minDragValues?: LengthInputBounds;
  autofocus?: boolean;
  /** Allowable strings for values */
  allowedNonUnitValues?: string[];

  /**
   * The value of the input on mount. The input will be controlled by this value until the
   * user changes it. If the user changes it, the input will be uncontrolled until this component
   * is rerendered with a new value for `value`.
   */
  value?: string | ReploMixedStyleValue | null;
  /**
   * The speed of the drag. This is used to calculate the distance the input will move when the user drags.
   */
  dragSpeed?: number;
};

type LengthInputSelectorProps = CommonProps;

const PARSE_UNIT_DEFAULTS = { value: "", unit: "" };

function useOverridableRef<T>(value: T) {
  const ref = React.useRef<T>(value);

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

type LengthInputSelectorContext = {
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  dragTrigger: CommonProps["dragTrigger"];
  draggingType: CommonProps["draggingType"];
  draggingDirection: CommonProps["draggingDirection"];
  field: CommonProps["field"];
  isDisabled: boolean;
  isReadOnly: boolean;
  onSubmit: (args: {
    value: string | null;
    currentlyPressedHotkey: LengthInputModifierHotkey | null;
    shouldBlur: boolean;
  }) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  setInputValue: (value: string) => void;
  parseValue: (value: string | number, unit?: string) => string;
  isMouseDown: boolean;
  enablePreviewCss: (event: KeyEvent, force?: boolean) => void;
  disablePreviewCss: (event: KeyEvent) => void;
  setPreviewCss: (event: KeyEvent, value: string) => void;
  metrics: CommonProps["metrics"];
  allowedNonUnitValues: CommonProps["allowedNonUnitValues"];
  unitDefaults: CommonProps["unitDefaults"];
  maxValues: CommonProps["maxValues"];
  minValues: CommonProps["minValues"];
  menuOptions: CommonProps["menuOptions"];
  defaultUnit: string;
  currentUnit: string;
  anchorValue: CommonProps["anchorValue"];
  isFocused: boolean;
  setIsFocused: (value: boolean) => void;
  isDragging: boolean;
  isCurrentlySubmittingValueRef: React.RefObject<boolean>;
  resetValue: () => void;
  isMixedStyleValue: boolean;
};
const LengthInputSelectorContext =
  React.createContext<LengthInputSelectorContext | null>(null);

const LengthInputSelectorRoot: React.FC<
  React.PropsWithChildren<
    Omit<LengthInputSelectorProps, LengthInputSelectorPropsNeededForInput>
  >
> = (props) => {
  const internalInputRef = React.useRef<HTMLInputElement | null>(null);
  const inputRef: React.RefObject<HTMLInputElement> =
    props.inputRef ?? internalInputRef;
  const {
    enableCanvasPreviewCSSProperties,
    disableCanvasPreviewCSSProperties,
    setPreviewCSSPropertyValue,
  } = useInCanvasPreview();

  const defaultUnit = props.metrics?.[0] ?? "px";

  /**
   * NOTE (Kevin, 2025-12-08): Check if props.value is null and there's a null-valued
   * menu option (like "Auto"). If so, use the label as the input value instead of
   * parsing null to defaults.
   */
  const nullValueMenuOption = props.menuOptions?.find(
    (opt) => opt.value === null,
  );
  const shouldDisplayMenuOptionLabel =
    props.value === null && nullValueMenuOption !== undefined;

  const { value: initialValue, unit: currentUnit } = parseUnit(
    !isMixedStyleValue(props.value) ? props.value ?? null : null,
    props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
    props.field,
    defaultUnit,
    undefined,
    props.metrics,
    props.allowedNonUnitValues,
  );

  // NOTE (Fran, 2022-08-03): We have 2 values, the input value is the value used
  // to show in the input and the current value to use when we need to update
  // the value without submiting it.
  const firstInputValue = shouldDisplayMenuOptionLabel
    ? nullValueMenuOption.label
    : `${initialValue}${currentUnit}`;
  const resolvedInputValue =
    firstInputValue === props.resetValue ? "" : firstInputValue;
  const currentValueRef = useOverridableRef(resolvedInputValue);

  const [inputValue, setInputValue] = useOverridableState(
    !isMixedStyleValue(props.value) ? resolvedInputValue : "Mixed",
  );
  const [lastDragStart, setLastDragStart] = React.useState<Position | null>(
    null,
  );
  // NOTE (Andrew, 2026-01-09, REPL-20714): Track which value in a comma-separated pair is being dragged.
  // Captured on mousedown so it stays consistent throughout the drag operation.
  const dragTargetIsFirstValue = React.useRef<boolean>(true);
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const isCurrentlySubmittingValueRef = React.useRef(false);
  const [lastHotkey, setLastHotkey] = React.useState<Hotkey | "none">(null);
  // Note (Sebas, 2022-11-17): This state is for detecting if we have to
  // run enableCanvasPreviewCSSProperties() or if we should run
  // setPreviewCSSPropertyValue() instead when pressing arrow keys
  const [isPreviewEnabled, setIsPreviewEnabled] = React.useState(false);
  const dispatch = useEditorDispatch();

  const actions = {
    altKey:
      props.previewablePropertiesForOption ??
      (props.previewProperty ? [props.previewProperty] : []),
    shiftKey:
      props.previewablePropertiesForShift ??
      (props.previewProperty ? [props.previewProperty] : []),
    none: props.previewProperty ? [props.previewProperty] : [],
  };

  const enablePreviewCss = (event: KeyEvent, force?: boolean) => {
    if ((!isPreviewEnabled || force) && props.previewProperty) {
      setIsPreviewEnabled(true);
      const hotkey = getHotKey(event);
      const hotKeyOrNone = hotkey ?? "none";
      enableCanvasPreviewCSSProperties(
        actions[hotKeyOrNone],
        currentValueRef.current || `0${defaultUnit}`,
        props.previewSubProperty,
        props.previewPropertyIndex,
      );
      props.onPreviewChange?.(
        currentValueRef.current || `0${defaultUnit}`,
        hotkey,
      );
      setLastHotkey(hotKeyOrNone);
    }
  };

  const setPreviewCss = (event: KeyEvent, value: string) => {
    if (props.previewProperty) {
      const hotkey = getHotKey(event);
      const hotKeyOrNone = hotkey ?? "none";
      // Note (Sebas, 2022-11-22): In case the user switches between
      // hotkeys without releasing the click we need to run enablePreviewCss
      // again to update to the correct previewable properties
      if (hotKeyOrNone !== lastHotkey) {
        enablePreviewCss(event, true);
      } else {
        setPreviewCSSPropertyValue(
          actions[hotKeyOrNone],
          value ?? (currentValueRef.current || `0${defaultUnit}`),
          props.previewSubProperty,
          props.previewPropertyIndex,
        );
      }
      props.onPreviewChange?.(
        value ?? (currentValueRef.current || `0${defaultUnit}`),
        hotkey,
      );
      setLastHotkey(hotKeyOrNone);
    }
  };

  const disablePreviewCss = (event: KeyEvent) => {
    if (props.previewProperty && isPreviewEnabled) {
      const hotkey = getHotKey(event);
      disableCanvasPreviewCSSProperties(actions[hotkey ?? "none"]);
      setLastHotkey(null);
      setIsPreviewEnabled(false);
    }
  };

  const {
    draggingType = "vertical",
    draggingDirection,
    maxValues,
    minValues,
    maxDragValues,
    minDragValues,
  } = props;

  const shouldAllowNegativeValue = props.allowsNegativeValue ?? true;

  /**
   * Used to parse the value in the current unit.
   *
   * @private
   */
  const parseValue = (value: string | number, unit?: string) => {
    if (Number.isNaN(Number.parseFloat(`${value}`))) {
      return `${clampMinMax(
        value,
        maxValues?.[currentUnit],
        minValues?.[currentUnit],
      )}`;
    }

    const { unit: parsedUnit } = parseUnit(
      String(value),
      props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
      props.field,
      defaultUnit,
      undefined,
      props.metrics,
      props.allowedNonUnitValues,
    );

    let unitToApply =
      parsedUnit ?? currentUnit ?? props.unitDefaults?.unit ?? "px";

    if (unit) {
      unitToApply = unit;
    }

    return `${clampMinMax(
      Number.parseFloat(`${value}`),
      maxValues?.[currentUnit],
      minValues?.[currentUnit],
    )}${unitToApply}`;
  };

  /**
   * Used to submit the value in all the use cases. Also fire the onChange prop
   * and blur the input. Also use transformValue prop.
   *
   * @param {string} value
   * @param {string | null} hotkey
   * @param {boolean} shouldBlur
   * @private
   */

  const onSubmit = (args: {
    value: string | null;
    currentlyPressedHotkey: LengthInputModifierHotkey | null;
    shouldBlur: boolean;
  }) => {
    const { value, currentlyPressedHotkey: hotkey, shouldBlur } = args;

    // NOTE (Kevin, 2025-12-08): Handle null value specially for "Auto" options.
    // When null is submitted (e.g., from a menu option with value: null),
    // pass null directly to onChange without parsing, as parseUnit would convert it
    // to a default value.
    if (value === null) {
      const nullValueMenuOption = props.menuOptions?.find(
        (opt) => opt.value === null,
      );
      const menuOptionLabel = nullValueMenuOption?.label ?? "";
      currentValueRef.current = menuOptionLabel;
      props.onChange(value, hotkey);
      if (shouldBlur) {
        isCurrentlySubmittingValueRef.current = true;
        inputRef.current?.blur();
        isCurrentlySubmittingValueRef.current = false;
      }
      return;
    }

    // NOTE (Andrew, 2026-01-09, REPL-20714): Handle comma-separated values like "18, 22" for mixed spacing.
    // Pass these through as-is without parsing, since parseUnit doesn't understand this format.
    if (parseCommaSeparatedValues(value)) {
      currentValueRef.current = value;
      setInputValue(value);
      if (shouldBlur) {
        setIsFocused(false);
      }
      props.onChange(value, hotkey);
      if (shouldBlur) {
        isCurrentlySubmittingValueRef.current = true;
        inputRef.current?.blur();
        isCurrentlySubmittingValueRef.current = false;
      }
      return;
    }

    const tokens = parseUnit(
      value,
      props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
      props.field,
      defaultUnit,
      props.resetValue,
      props.metrics,
      props.allowedNonUnitValues,
    );

    if (
      !shouldAllowNegativeValue &&
      Number.parseFloat(String(tokens.value)) < 0
    ) {
      tokens.value = 0;
    }

    let newInputValue = parseValue(tokens.value, tokens.unit);

    if (props.transformValue) {
      newInputValue = props.transformValue(newInputValue);
    }

    setInputValue(newInputValue);
    if (shouldBlur) {
      setIsFocused(false);
    }

    if (
      newInputValue === currentValueRef.current ||
      (currentValueRef.current &&
        currentValueRef.current === props.resetValue) ||
      (!currentValueRef.current &&
        (newInputValue === props.resetValue ||
          parseFloat(newInputValue) === parseFloat(props.resetValue!)))
    ) {
      return;
    }
    // NOTE (Fran, 2022-07-12): We need to set the current value so if the next
    // time the user try to reset the value or put the same value don't call the
    // onChange again.
    currentValueRef.current = newInputValue;
    props.onChange(newInputValue, hotkey);
    if (shouldBlur) {
      isCurrentlySubmittingValueRef.current = true;
      // NOTE (Fran, 2022-08-03): The blur of the input is only do it in the
      // onSubmit function to prevent double call of this function.
      inputRef.current?.blur();
      isCurrentlySubmittingValueRef.current = false;
    }
  };

  const shouldInputDrag =
    Boolean(draggingType || draggingDirection) && !props.isReadOnly;

  /**
   * Access to the prop resetValue and set this value.
   * @private
   */
  const resetValue = () => {
    const { resetValue } = props;
    if (resetValue === undefined) {
      return;
    }

    onSubmit({
      value: resetValue ? parseValue(resetValue) : null,
      currentlyPressedHotkey: null,
      shouldBlur: true,
    });
  };

  /**
   * Used to access and update the value when the user is dragging the input.
   * @private
   */
  const getUpdatedValue = (
    clientX: number,
    clientY: number,
  ): number | string => {
    const { draggingType = "vertical", draggingDirection = "positive" } = props;
    const currentRefValue = currentValueRef.current || props.anchorValue || "0";

    // NOTE (Andrew, 2026-01-09, REPL-20714): Handle comma-separated values like "10, 16" for mixed spacing values.
    const parsed = parseCommaSeparatedValues(currentRefValue);
    if (parsed) {
      const clientValue = draggingType === "horizontal" ? clientX : clientY;
      const snapshot =
        draggingType === "horizontal"
          ? lastDragStart?.x ?? 0
          : lastDragStart?.y ?? 0;
      let sign = draggingDirection === "negative" ? -1 : 1;
      if (draggingType === "horizontal") {
        sign *= -1;
      }
      const speed = props.dragSpeed ?? 1.5;
      const delta = sign * Math.floor((clientValue - snapshot) * speed);

      const minValue = minDragValues?.px ?? minValues?.px;
      const clampValue = (val: number) => {
        return minValue !== undefined ? Math.max(val, minValue) : val;
      };

      if (dragTargetIsFirstValue.current) {
        return formatCommaSeparatedValues(
          clampValue(parsed.first - delta),
          parsed.second,
        );
      }
      return formatCommaSeparatedValues(
        parsed.first,
        clampValue(parsed.second - delta),
      );
    }

    // Note (Fran, 2022-04-26): We use || instead of ?? because some
    // of these could be an empty string and is ok, because that is nothing to
    // the input. In this cases we need nothing instead of a value of 0.
    // The empty string is handle in the function parseUnit
    const value = Number.parseFloat(
      `${
        parseUnit(
          currentRefValue,
          props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
          props.field,
          defaultUnit,
          props.resetValue,
          props.metrics,
          props.allowedNonUnitValues,
        ).value
      }`,
    );

    if (Number.isNaN(value)) {
      return currentValueRef.current;
    }
    const clientValue = draggingType === "horizontal" ? clientX : clientY;
    const snapshot =
      draggingType === "horizontal"
        ? lastDragStart?.x ?? 0
        : lastDragStart?.y ?? 0;
    let sign = draggingDirection === "negative" ? -1 : 1;
    if (draggingType === "horizontal") {
      sign *= -1;
    }
    const speed = props.dragSpeed ?? 1.5;
    return value - sign * Math.floor((clientValue - snapshot) * speed);
  };

  /**
   * Trigger: Fired when the user starts dragging
   * Functionality: Sets the cursor and enables to use the preview field
   * functionality in the canvas.
   * @private
   */
  const onMouseDown = (event: React.MouseEvent) => {
    if (props.isDisabled) {
      return;
    }
    props.onDragStart?.();
    const { draggingDirection, draggingType } = props;

    // NOTE (Andrew, 2026-01-09, REPL-20714): Capture which value in comma-separated pair to drag
    const currentRefValue = currentValueRef.current || "";
    if (parseCommaSeparatedValues(currentRefValue)) {
      const cursorPosition = inputRef.current?.selectionStart ?? 0;
      dragTargetIsFirstValue.current = isCursorOnFirstValue(
        currentRefValue,
        cursorPosition,
      );
    }

    setIsMouseDown(true);
    setLastDragStart({ x: event.clientX, y: event.clientY });

    if (shouldInputDrag && draggingType) {
      dispatch(setDraggingCursor(getCursor(draggingType, draggingDirection)));
    }
    enablePreviewCss(event);
  };

  const onDragEnd = () => {
    props.onDragEnd?.();
    inputRef.current?.focus();
  };

  /**
   * Have all the logic to finish the drag, disable the preview in the canvas
   * and submit the value
   * @private
   */
  const onMouseUp = useEffectEvent((event: MouseEvent) => {
    const isInputDraggable = shouldInputDrag && isDragging;
    const finishUpdatingState = () => {
      dispatch(setDraggingCursor(null));
      setIsMouseDown(false);
      setIsDragging(false);
    };

    disablePreviewCss(event);

    const hotkey = getHotKey(event);

    // Note (Fran: 2022-09-02): We need to handle the reset with alt key only
    // here to avoid unwanted resetting when we are dragging on the input.
    // Unless we have a startEnhancer, in that case we need to allowed because
    // is the only way i found to do it.
    if (hotkey === "altKey" && !isDragging) {
      finishUpdatingState();
      resetValue();
      onDragEnd();
      return;
    }

    if (!isInputDraggable) {
      finishUpdatingState();
      onDragEnd();
      return;
    }
    finishUpdatingState();
    onSubmit({
      // NOTE (Andrew, 2026-01-09, REPL-20714): Preserve comma-separated values for mixed spacing
      value: parseCommaSeparatedValues(inputValue)
        ? inputValue
        : parseValue(inputValue),
      currentlyPressedHotkey: hotkey,
      shouldBlur: true,
    });
    onDragEnd();
  });

  const onMouseMove = useEffectEvent((event: MouseEvent) => {
    if (!shouldInputDrag) {
      return;
    }
    const { clientX, clientY } = event;
    let updatedValue = getUpdatedValue(clientX, clientY);

    // NOTE (Andrew, 2026-01-09, REPL-20714): Handle comma-separated values from getUpdatedValue
    if (typeof updatedValue === "string") {
      const newParsed = parseCommaSeparatedValues(updatedValue);
      if (newParsed) {
        const hotkey = getHotKey(event);
        setInputValue(updatedValue);
        setIsDragging(true);
        // NOTE (Andrew, 2026-01-09, REPL-20714): Use the drag target ref to determine which value to preview
        const previewVal = dragTargetIsFirstValue.current
          ? newParsed.first
          : newParsed.second;
        setPreviewCss(event, `${previewVal}px`);
        props.onPreviewChange?.(updatedValue, hotkey);
        return;
      }
    }

    if (Number.isNaN(Number.parseFloat(`${updatedValue}`))) {
      return;
    }

    // Note (Chance 2023-08-10) A type error surfaced here after upgrading to TS
    // 5.0 because TS doesn't want us using numeric comparison operators on
    // non-numeric types. We weren't casting or stringifying `updatedValue`
    // before so I didn't change this to avoid potential bugs or behavior
    // changes, but this won't result in a runtime error so I just casted to a
    // number and moved on. Consider improving this.
    if (!shouldAllowNegativeValue && (updatedValue as number) < 0) {
      updatedValue = 0;
    }
    // NOTE (Sebas, 2024-04-24): Previously we were using ?? here what caused the
    // preview to not work becaue we were using the wrong unit (an empty string)
    // instead of the unit from metrics.
    const unit = currentUnit || props.metrics?.[0];
    updatedValue = clampMinMax(
      updatedValue,
      maxDragValues?.[unit || ""],
      minDragValues?.[unit || ""],
    )!;

    const hotkey = getHotKey(event);
    const newValue = parseValue(
      updatedValue,
      currentUnit ?? props.metrics?.[0] ?? "px",
    );
    const value = Number.parseFloat(`${currentValueRef.current || 0}`);
    const difference = Math.abs(value - (updatedValue as number));
    setInputValue(newValue);
    // NOTE (Fran, 2022-09-05): If we drag with a hotkey we should always set
    // isDragging in true, but if is not, we have to do only if the difference
    // is equal or more than one to prevent unexpected behaviors.
    setIsDragging(hotkey ? true : difference >= 1);
    setPreviewCss(event, newValue);
    props.onPreviewChange?.(newValue, hotkey);
  });

  React.useEffect(() => {
    if (!isMouseDown) {
      return;
    }

    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [isMouseDown, onMouseMove, onMouseUp]);

  return (
    <LengthInputSelectorContext.Provider
      value={{
        onMouseDown,
        draggingDirection: props.draggingDirection,
        dragTrigger: props.dragTrigger,
        draggingType: props.draggingType,
        field: props.field,
        isDisabled: props.isDisabled ?? false,
        isReadOnly: props.isReadOnly ?? false,
        onSubmit,
        inputRef,
        inputValue,
        setInputValue,
        parseValue,
        isMouseDown,
        enablePreviewCss,
        disablePreviewCss,
        setPreviewCss,
        metrics: props.metrics,
        allowedNonUnitValues: props.allowedNonUnitValues,
        unitDefaults: props.unitDefaults,
        defaultUnit,
        maxValues: props.maxValues,
        minValues: props.minValues,
        menuOptions: props.menuOptions,
        currentUnit: currentUnit,
        anchorValue: props.anchorValue,
        isFocused,
        setIsFocused,
        isDragging,
        isCurrentlySubmittingValueRef,
        resetValue,
        isMixedStyleValue: isMixedStyleValue(props.value),
      }}
    >
      {props.children}
    </LengthInputSelectorContext.Provider>
  );
};

const LengthInputSelectorDraggable: React.FC<
  React.PropsWithChildren<{
    className?: string;
    dataTestId?: string;
  }>
> = ({ children, dataTestId, className }) => {
  const { onMouseDown, isDisabled } = useRequiredContext(
    LengthInputSelectorContext,
  );
  return (
    <div
      className={twMerge(
        "flex cursor-ns-resize",
        isDisabled && "cursor-not-allowed",
        className,
      )}
      onMouseDown={onMouseDown}
      data-testid={dataTestId ?? "draggable-component-wrapper"}
    >
      {children}
    </div>
  );
};

type LengthInputSelectorPropsNeededForInput =
  | "startEnhancer"
  | "endEnhancer"
  | "placeholder"
  | "autofocus"
  | "backgroundColor";

const LengthInputSelectorInput: React.FC<
  Pick<LengthInputSelectorProps, LengthInputSelectorPropsNeededForInput> & {
    className?: string;
  }
> = (props) => {
  const {
    dragTrigger,
    draggingType,
    draggingDirection,
    isDisabled,
    isReadOnly,
    field,
    onSubmit,
    inputRef,
    isMouseDown,
    inputValue,
    setInputValue,
    parseValue,
    enablePreviewCss,
    disablePreviewCss,
    setPreviewCss,
    metrics,
    allowedNonUnitValues,
    unitDefaults,
    maxValues,
    minValues,
    menuOptions,
    defaultUnit,
    currentUnit,
    anchorValue,
    isFocused,
    setIsFocused,
    isDragging,
    isCurrentlySubmittingValueRef,
    resetValue,
  } = useRequiredContext(LengthInputSelectorContext);

  // Note (Kevin, 12/2/2025): Compute display value from menuOptions when not focused
  const displayValue = getDisplayValueFromMenuOptions({
    isRawInputFocused: isFocused,
    inputValue,
    menuOptions,
    unitDefaults,
    field,
    defaultUnit,
    metrics,
    allowedNonUnitValues,
  });

  const allowDragOnLabel = dragTrigger === "label";
  const shouldAllowDragOnStartEnhancer =
    !allowDragOnLabel &&
    ((!dragTrigger && Boolean(draggingType)) ||
      dragTrigger === "startEnhancer" ||
      (Boolean(props.startEnhancer) && dragTrigger !== "none"));

  const inputStyles: Record<string, string> = {
    backgroundColor: props.backgroundColor ?? "transparent",
    cursor: isReadOnly ? "pointer" : getCursor(draggingType, draggingDirection),
    caretColor: isMouseDown ? "transparent" : "auto",
  };

  const [inputContainerRef, { width }] = useMeasure();

  /**
   * Have the logic when we press need to use key down and key up to increase
   * or decrease values.
   * @private
   */
  const _onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      return;
    }
    if (event.key === "Enter") {
      // NOTE (Andrew, 2026-01-09, REPL-20714): Normalize "10px, 20" -> "10, 20" on Enter, same as blur
      const normalized = normalizeCommaSeparatedInput(inputValue);
      onSubmit({
        value:
          normalized ??
          (parseCommaSeparatedValues(inputValue)
            ? inputValue
            : parseValue(inputValue)),
        currentlyPressedHotkey: null,
        shouldBlur: true,
      });
      return;
    }

    const transform = (currentValue: number, valueToChange: number): number => {
      if (event.key === "ArrowDown") {
        return clampMinMax(
          currentValue - valueToChange,
          maxValues?.[currentUnit],
          minValues?.[currentUnit],
        )!;
      }
      return clampMinMax(
        currentValue + valueToChange,
        maxValues?.[currentUnit],
        minValues?.[currentUnit],
      )!;
    };

    // NOTE (Andrew, 2026-01-09, REPL-20714): Handle comma-separated values like "10, 16" for mixed spacing values.
    // Increment/decrement only the value where the cursor is positioned.
    const parsed = parseCommaSeparatedValues(inputValue);
    if (parsed) {
      const cursorPosition = inputRef.current?.selectionStart ?? 0;
      const isFirst = isCursorOnFirstValue(inputValue, cursorPosition);
      const newFirst = isFirst ? transform(parsed.first, 1) : parsed.first;
      const newSecond = isFirst ? parsed.second : transform(parsed.second, 1);
      const newValue = formatCommaSeparatedValues(newFirst, newSecond);
      const previewVal = isFirst ? newFirst : newSecond;

      setInputValue(newValue);
      // NOTE (Andrew, 2026-01-09, REPL-20714): Restore cursor position after React re-renders,
      // otherwise cursor jumps to start of input making repeated increments change wrong value.
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
      });
      enablePreviewCss(event);
      setPreviewCss(event, `${previewVal}px`);
      return;
    }

    let valueToStartFrom = Number.parseFloat(inputValue);
    let unitToIncrementBy = parseUnit(
      inputValue,
      unitDefaults ?? PARSE_UNIT_DEFAULTS,
      field,
      defaultUnit,
      undefined,
      metrics,
      allowedNonUnitValues,
    ).unit;

    if (!inputValue) {
      if (!anchorValue) {
        return;
      }
      const { value: anchorValueParsed, parsedUnit: anchorUnit } = parseUnit(
        anchorValue,
        unitDefaults ?? PARSE_UNIT_DEFAULTS,
        field,
        defaultUnit,
        undefined,
        metrics,
        allowedNonUnitValues,
      );
      const anchorValueNumber = Number.parseFloat(`${anchorValueParsed}`);

      if (!Number.isNaN(anchorValueNumber)) {
        valueToStartFrom = anchorValueNumber;
        unitToIncrementBy = anchorUnit ?? currentUnit;
      }
    }
    let newValue = "";
    if (["", "px", "%", "vw", "vh"].includes(unitToIncrementBy)) {
      const value = transform(valueToStartFrom, 1);
      newValue = parseValue(value, unitToIncrementBy);
    } else {
      const value = round(transform(valueToStartFrom, 0.1), 1);
      newValue = parseValue(value, unitToIncrementBy);
    }
    setInputValue(newValue);
    enablePreviewCss(event);
    setPreviewCss(event, newValue);
  };

  /**
   * NOTE (Andrew, 2026-01-09, REPL-20714): Submit the value when finish the key up/down logic and when we release the
   * alt or shift key when we are dragging
   * @private
   */
  const _onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isArrowKey = ["ArrowUp", "ArrowDown"].includes(event.key);
    const isDraggableUnit = CSS_LENGTH_TYPES.concat("").includes(currentUnit);
    const isHotkeyPressed = Boolean(getHotKey(event));

    if ((isArrowKey && isDraggableUnit) || (isHotkeyPressed && isDragging)) {
      disablePreviewCss(event);
      onSubmit({
        value: inputValue,
        currentlyPressedHotkey: getHotKey(event),
        shouldBlur: false,
      });
    }
  };

  const startEnhancer = props.startEnhancer;

  const enhancerMenuItems =
    menuOptions &&
    menuOptions.map(
      ({
        label,
        value: menuValue,
        isDisabled: optionDisabled,
        icon,
      }): MenuItem => ({
        id: label,
        type: "leaf",
        title: label,
        disabled: optionDisabled,
        variant: "default" as const,
        size: "sm" as const,
        startEnhancer: icon,
        selected:
          String(displayValue) === label ||
          (menuValue !== null && String(displayValue) === String(menuValue)),
        onSelect: () => {
          // NOTE (Kevin, 2025-12-08): Handle null value specially for "Auto" options.
          // When null is selected, display the label directly instead of going through
          // onSubmit which would convert null to a default value via parseUnit.
          if (menuValue === null) {
            setInputValue(label);
            setIsFocused(false);
          }
          onSubmit({
            value: menuValue,
            currentlyPressedHotkey: null,
            shouldBlur: true,
          });
        },
      }),
    );

  return (
    <LengthInputSelectorDraggable className="flex h-full w-full">
      <DeprecatedInput
        startEnhancer={
          startEnhancer && shouldAllowDragOnStartEnhancer ? (
            <LengthInputSelectorDraggable dataTestId={`${field}-startEnhancer`}>
              {startEnhancer}
            </LengthInputSelectorDraggable>
          ) : (
            startEnhancer
          )
        }
        inputContainerRef={inputContainerRef}
        endEnhancer={
          (enhancerMenuItems &&
            (() => {
              return (
                <ChevronMenuIndicator
                  items={enhancerMenuItems}
                  menuWidth={width}
                  isDisabled={isDisabled}
                  alignOffset={-8}
                  sideOffset={8}
                />
              );
            })) ||
          props.endEnhancer
        }
        ref={inputRef}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        placeholder={props.placeholder ?? "auto"}
        value={displayValue}
        onPaste={(e) => {
          const pastedText = e.clipboardData.getData("text");
          // NOTE (Andrew, 2026-01-09, REPL-20714): Normalize "10px, 20" -> "10, 20" on paste
          const normalized = normalizeCommaSeparatedInput(pastedText);
          onSubmit({
            value:
              normalized ??
              (parseCommaSeparatedValues(pastedText)
                ? pastedText
                : parseValue(pastedText)),
            currentlyPressedHotkey: null,
            shouldBlur: true,
          });
        }}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        unsafe_className={twMerge(
          "focus-within:ring-0 focus-within:ring-transparent focus-within:border-component-border",
          isReadOnly && "hover:bg-surface",
          props.className,
        )}
        unsafe_inputClassName={props.className}
        unsafe_style={inputStyles}
        onKeyDown={_onKeyDown}
        onKeyUp={_onKeyUp}
        shouldSelectTextOnFocus
        onFocus={() => {
          if (!isDisabled) {
            setIsFocused(true);
            // NOTE (Derek, 2026-01-07, REPL-21309): Select text right after React re-renders.
            // This fixes the issue where clicking an input showing "None" changes to
            // "0px" but the text isn't highlighted.
            requestAnimationFrame(() => inputRef.current?.select());
          }
        }}
        onBlur={(e) => {
          e.preventDefault();
          setIsFocused(false);
          // Note (Noah, 2022-08-13, REPL-3548): If we're not currently in the
          // middle of a submit (aka, we initiated the blur ourselves), then
          // submit the value. We don't want to submit if we're already in the middle
          // of one because that would case duplicate updates.
          if (!isCurrentlySubmittingValueRef.current) {
            // NOTE (Andrew, 2026-01-09, REPL-20714): Normalize "24px, 20" -> "24, 20" on blur
            const normalized = normalizeCommaSeparatedInput(inputValue);
            onSubmit({
              value: normalized ?? parseValue(inputValue),
              currentlyPressedHotkey: null,
              shouldBlur: false,
            });
          }
        }}
        onClick={(e) => {
          // Note (Fran, 2022-09-02): We only need to reset the value when we
          // click the input if we have startEnhancer. Because in other cases
          // we need to handle only where the drag
          if (
            getHotKey(e) === "altKey" &&
            !isDragging &&
            Boolean(props.startEnhancer)
          ) {
            resetValue();
          }
        }}
        allowsDragOnStartEnhancer={shouldAllowDragOnStartEnhancer}
        isFocused={isFocused}
        data-testid={`${field}-input`}
        autoFocus={props.autofocus}
      />
    </LengthInputSelectorDraggable>
  );
};

// NOTE (Fran, 2022-08-03): More docs on https://www.notion.so/replo/LegnthInputSelector-Docs-b0be6b93767a46a8a1854f3f1f6833ce
export const LengthInputSelector = (props: LengthInputSelectorProps) => {
  const draftComponents = useEditorSelector(selectDraftComponents);
  const getAttribute = useEditorSelector(selectGetAttribute);

  let { value } = props;

  // NOTE (Martin 2025-01-29): If there are selected components we need to
  // take into consideration the fact that the value could be mixed.
  // TODO (Martin 2025-01-20): props.value itself should already be a proper
  // value or mixed, we shouldn't need to check for mixed values here.
  if (draftComponents.length > 0) {
    const parsedField = props.field.split(".")[1] as RuntimeStyleAttribute;
    const { firstValue, hasMixedValues } = checkForMixedValues(
      draftComponents ?? [],
      (component) => getEdgeAttribute(component, parsedField, getAttribute),
    );

    value = hasMixedValues
      ? REPLO_MIXED_STYLE_VALUE
      : props.value ?? firstValue;
  }

  return (
    <LengthInputSelectorRoot {...props} value={value}>
      <div
        className={twMerge(
          "flex flex-row items-center justify-center",
          props.className,
        )}
        style={props.style}
      >
        {props.label &&
          (props.dragTrigger === "label" ? (
            <LengthInputSelectorDraggable dataTestId={`${props.field}-label`}>
              {props.label}
            </LengthInputSelectorDraggable>
          ) : (
            props.label
          ))}
        <LengthInputSelectorInput
          className={props.inputClassName}
          startEnhancer={props.startEnhancer}
          endEnhancer={props.endEnhancer}
          placeholder={props.placeholder}
          autofocus={props.autofocus}
          backgroundColor={props.backgroundColor}
        />
      </div>
    </LengthInputSelectorRoot>
  );
};

LengthInputSelector.Root = LengthInputSelectorRoot;
LengthInputSelector.Input = LengthInputSelectorInput;
LengthInputSelector.DraggableArea = LengthInputSelectorDraggable;
